import { Formik } from "formik";
import ErrorBox from "lib/components/common/error-box";
import FlexBox from "lib/components/common/flex-box";
import LoadingBox from "lib/components/common/loading-box";
import RequiredField from "lib/components/common/required-field";
import SimpleModal from "lib/components/common/simple-modal";
import SimpleTextbox from "lib/components/common/simple-textbox";
import {
  EMAIL_VALIDATION_SCHEMA,
  INVITE_PRACTITIONER_VALIDATION_SCHEME,
  NAME_VALIDATION_SCHEMA,
} from "lib/constants/validation-constants";
import { FC } from "react";
import { Button } from "react-bootstrap";
import { Form } from "react-bootstrap";
import { toast } from "sonner";

interface PractitionerInfoProps {
  open: boolean;
  onClose: () => void;
  onInvite: (data: { email: string; name: string }) => void;
  error: string | null;
  loading: boolean;
}

const DEFAULT_VALUES = { email: "", name: "" };

const InvitePractitionerModal: FC<PractitionerInfoProps> = ({
  open,
  onClose,
  onInvite,
  error,
  loading,
}) => {
  const Footer = () => (
    <FlexBox justifyContent="between" className="w-100" alignItems="center">
      <Button
        type="button"
        variant="light"
        className="w-auto"
        onClick={onClose}
      >
        Cancel
      </Button>
      <Button type="submit" form="invite-form" className="w-auto">
        Send Invite
      </Button>
    </FlexBox>
  );

  return (
    <SimpleModal
      footer={<Footer />}
      open={open}
      close={onClose}
      title="Invite Practitioner"
      size="lg"
    >
      {loading ? (
        <LoadingBox message="Loading practitioners..." />
      ) : (
        <Formik
          initialValues={DEFAULT_VALUES}
          validationSchema={INVITE_PRACTITIONER_VALIDATION_SCHEME}
          onSubmit={(values, { setSubmitting, setFieldError, resetForm }) => {
            const trimmedEmail = values.email.trim();
            const trimmedName = values.name.trim();

            if (!trimmedEmail || !trimmedName) {
              toast.error("Please fill in all required fields.");
              return;
            }

            onInvite({ email: trimmedEmail, name: trimmedName });
            setSubmitting(false);
            resetForm();
          }}
        >
          {({ values, handleChange, handleSubmit, errors, isSubmitting }) => (
            <Form id="invite-form" onSubmit={handleSubmit}>
              <FlexBox
                justifyContent="center"
                alignItems="center"
                className="w-100 gap-4"
              >
                <SimpleTextbox
                  label="Name"
                  name="name"
                  value={values.name}
                  onChange={handleChange}
                  placeholder="Enter practitioner name"
                  required
                  validationSchema={NAME_VALIDATION_SCHEMA(
                    "Practitioner name is required"
                  )}
                />

                <SimpleTextbox
                  label="Email"
                  name="email"
                  value={values.email}
                  onChange={handleChange}
                  placeholder="Enter practitioner email"
                  required
                  validationSchema={EMAIL_VALIDATION_SCHEMA}
                />
              </FlexBox>

              {(errors.email || error) && (
                <ErrorBox className="mt-4" message={errors.email || error} />
              )}
            </Form>
          )}
        </Formik>
      )}
    </SimpleModal>
  );
};

export default InvitePractitionerModal;
